import axios, { AxiosError } from "axios";
import { API_HOST } from "./config";
import { InputEntity, RequestEntity } from "../dtos/index";
import { LOCAL_STORAGE_AUTH_TOKEN_KEY } from "../auth/storage";

const fileIdPrefix = "--file-upload-id-";

export default class Api {
  public fileId: number = 0;

  public toFormData(
    obj: any,
    formData: FormData = new FormData(),
    parentKey: string = ""
  ): FormData {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const fullKey = parentKey ? `${parentKey}[${key}]` : key;
        const value = obj[key];

        if (typeof value === "boolean") {
          formData.append(fullKey, `${value ? 1 : 0}`);
        } else if (value instanceof Date) {
          const pad = (num: number) => String(num).padStart(2, "0");

          const formattedDate = `${value.getFullYear()}-${pad(
            value.getMonth() + 1
          )}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(
            value.getMinutes()
            // )}:${pad(value.getSeconds())}`;
          )}:00`;

          formData.append(fullKey, formattedDate);
        } else if (value instanceof File || value instanceof Blob) {
          formData.append(fullKey, fileIdPrefix + this.fileId);
          formData.append(fileIdPrefix + this.fileId++, value);
        } else if (typeof value === "object" && value !== null) {
          this.toFormData(value, formData, fullKey);
        } else {
          if (value !== undefined && value !== null)
            formData.append(fullKey, value);
        }
      }
    }

    return formData;
  }

  constructor(
    protected readonly uri: string,
    protected readonly host: string = API_HOST
  ) {}

  public get endpoint(): string {
    return this.host + this.uri;
  }

  public async get(
    id: string | null | undefined = null,
    params: RequestEntity | null | undefined = null
  ): Promise<any> {
    try {
      let endpoint = this.endpoint;
      if (id !== null && id !== undefined && id.trim().length > 0) {
        endpoint += "/" + id.trim();
      }

      if (params === undefined || params === null)
        return await axios.get(endpoint, {
          withCredentials: true,
          headers: {
            ...{
              "Content-Type": "multipart/form-data",
              Accept: "application/json",
            },
            ...(localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY)
              ? {
                  Authorization:
                    "Bearer " +
                    localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY),
                }
              : {}),
          },
        });

      return await axios.get(endpoint, {
        withCredentials: true,
        headers: {
          ...{
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          ...(localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY)
            ? {
                Authorization:
                  "Bearer " +
                  localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY),
              }
            : {}),
        },
        params: Object.fromEntries(
          Object.entries(params).map(([key, value]) => [
            key,
            JSON.stringify(value),
          ])
        ),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response) {
          throw new ApiError(
            error.response.data?.message ?? JSON.stringify(error.response.data),
            error.response.status
          );
        }
        throw error;
      }
      throw error;
    }
  }

  public async post(
    data: InputEntity | null | undefined = null,
    params: RequestEntity | null | undefined = null
  ): Promise<any> {
    try {
      return await axios({
        method: "post",
        url: this.endpoint,
        data: data ? this.toFormData(data) : undefined,
        headers: {
          ...{
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          ...(localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY)
            ? {
                Authorization:
                  "Bearer " +
                  localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY),
              }
            : {}),
        },
        withCredentials: true,
        params: params
          ? Object.fromEntries(
              Object.entries(params).map(([key, value]) => [
                key,
                JSON.stringify(value),
              ])
            )
          : undefined,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response) {
          throw new ApiError(
            error.response.data?.message ?? JSON.stringify(error.response.data),
            error.response.status
          );
        }
        throw error;
      }
      throw error;
    }
  }

  public async put(
    id: string,
    data: InputEntity | null | undefined = null,
    params: RequestEntity | null | undefined = null
  ): Promise<any> {
    try {
      return await axios({
        method: "post",
        url: this.endpoint + "/" + id,
        data: this.toFormData({ ...(data ?? {}), _method: "PUT" }),
        headers: {
          ...{
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          ...(localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY)
            ? {
                Authorization:
                  "Bearer " +
                  localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY),
              }
            : {}),
        },
        withCredentials: true,
        params: params
          ? Object.fromEntries(
              Object.entries(params).map(([key, value]) => [
                key,
                JSON.stringify(value),
              ])
            )
          : undefined,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response) {
          throw new ApiError(
            error.response.data?.message ?? JSON.stringify(error.response.data),
            error.response.status
          );
        }
        throw error;
      }
      throw error;
    }
  }

  public async delete(
    id: string,
    params: RequestEntity | null | undefined = null
  ): Promise<any> {
    try {
      return await axios({
        method: "delete",
        url: this.endpoint + "/" + id,
        headers: {
          ...{
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          ...(localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY)
            ? {
                Authorization:
                  "Bearer " +
                  localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY),
              }
            : {}),
        },
        withCredentials: true,
        params: params
          ? Object.fromEntries(
              Object.entries(params).map(([key, value]) => [
                key,
                JSON.stringify(value),
              ])
            )
          : undefined,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response) {
          throw new ApiError(
            error.response.data?.message ?? JSON.stringify(error.response.data),
            error.response.status
          );
        }
        throw error;
      }
      throw error;
    }
  }
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = "ApiError";
  }
}
